/**
 * The right panel's decisions. **Pure**, so every case is checkable without a DOM.
 *
 * Same split as `thread-view.ts` and `connection-presentation.ts`: what to show
 * is decided here and asserted exhaustively in the server project; whether it
 * reaches the screen is the component test's job. A decision tested through a
 * render is a decision tested once, in whichever branch the fixture happened to
 * take.
 */
import type { PanelActivity, PanelIdentity } from '../../../src/services/conversation-panel.js'

/**
 * A person's name, or `null` when there is nobody.
 *
 * ⛔ RETURNS null RATHER THAN A PLACEHOLDER, so the caller omits the element.
 * "Unknown" or "-" sits exactly where a real name sits, which is the rule that
 * dropped the booking line and the list's empty timestamp.
 */
export function personName(
  first: string | null,
  last: string | null,
): string | null {
  const name = [first, last].filter((p) => p !== null && p.trim() !== '').join(' ')
  return name === '' ? null : name
}

/**
 * What the identity card shows for the assignee.
 *
 * ⛔ THREE STATES, AND `unassigned` IS A REAL ONE. Migration 0012 says so in its
 * own words - *"unassigned is a real and expected state, not a missing value"* -
 * so it gets a word rather than an omission. This is the opposite answer from
 * the name fallback above, and the difference is that **nobody being assigned is
 * a fact about the conversation, while nobody having a name is a gap in what we
 * know.**
 */
export type AssigneeRender =
  | { kind: 'assigned'; name: string }
  | { kind: 'unassigned' }

export function assigneeRender(identity: PanelIdentity): AssigneeRender {
  const name = personName(identity.assignedFirstName, identity.assignedLastName)
  return name === null ? { kind: 'unassigned' } : { kind: 'assigned', name }
}

/**
 * ⛔ THE SNOOZE ROW EXISTS ONLY WHEN SNOOZED, and the NOTE is separate from the
 * TIME.
 *
 * `snooze_until` and `snooze_note` are independent columns and the corpus has
 * 1,042 rows with both. A card that rendered the note without the time would say
 * "follow up on the quote" with no indication that this conversation is hidden
 * from the default list until Thursday, which is the fact that matters.
 */
export type SnoozeRender =
  | { kind: 'none' }
  | { kind: 'snoozed'; until: string; note: string | null }

export function snoozeRender(identity: PanelIdentity): SnoozeRender {
  if (identity.snoozeUntil === null) return { kind: 'none' }
  const note = identity.snoozeNote?.trim()
  return {
    kind: 'snoozed',
    until: identity.snoozeUntil,
    note: note === undefined || note === '' ? null : note,
  }
}

/**
 * ⛔ `noteRender` WAS DELETED HERE ON 2026-08-11, NOT LEFT BEHIND.
 *
 * The panel's Notes card is gone: it read the same conversation-scoped rows the
 * thread now renders inline, so the identical notes appeared twice - recorded as
 * `thread-notes-and-panel-notes-render-the-same-rows`. Amir ruled that the
 * panel's "5. Notes" means CONTACT-LEVEL notes, which have no table, no column
 * and no producer - see `contact-level-notes-have-no-storage`.
 *
 * ⚠️ THE DECISION WORTH RECORDING IS DELETING RATHER THAN KEEPING. Its empty-body
 * branch was genuinely good - an accidental empty submit rendered as a dated,
 * attributed card instead of a blank strip - and keeping it "for when contact
 * notes arrive" would have made it the fourth instance of the shape this ledger
 * already carries twice: `returnto-had-no-consumer` and
 * `scope-item-6-not-built`. **An export whose only caller was deleted is a rule
 * with no consumer**, and it will be rewritten against whatever shape contact
 * notes actually take rather than found and trusted.
 *
 * The thread's own note render is `rowKind()` in `thread-view.ts`, which is a
 * different job: it decides ATTRIBUTION across four actor cases, which is the
 * thing the panel version never had to do.
 */

/**
 * One line of the activity feed.
 *
 * ┌─ ⛔ A TAGGED UNION, NOT A STRING, AND THE REASON IS THE SAME AS THE THREAD'S │
 * │ BODY RENDER. Returning a formatted sentence here would put copy inside a    │
 * │ pure module and make every assertion a string comparison against wording    │
 * │ somebody will reword tomorrow. The component owns the words; this owns      │
 * │ which                                                                       │
 * │ shape applies and who the subjects are.                                     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ **THERE IS NO `default` BRANCH AND THERE MUST NOT BE.** An unrecognised
 * `event_type` returns `unknown` EXPLICITLY, carrying the verb, so a new type
 * shipped by a later phase renders as a dated line naming what happened rather
 * than vanishing. **A feed that silently drops rows it does not recognise is the
 * missed-event failure with a tidy UI**, and it is exactly what would happen
 * when CRM-BUILD adds `stage_changed`.
 *
 * ⚠️ AND THE CORPUS COULD NOT HAVE TOLD ANYONE THIS. Until 2026-08-08 every
 * event in the database was `status_changed`, so a switch with one arm and a
 * silent default would have passed every test that existed.
 */
export type ActivityRender =
  | { kind: 'statusChanged'; from: string | null; to: string; actor: string | null }
  | { kind: 'assigned'; from: string | null; to: string | null; actor: string | null }
  | { kind: 'unassigned'; from: string | null; actor: string | null }
  | { kind: 'unknown'; eventType: string; actor: string | null }

export function activityRender(event: PanelActivity): ActivityRender {
  const actor = personName(event.actorFirstName, event.actorLastName)
  const from = personName(event.fromFirstName, event.fromLastName)
  const to = personName(event.toFirstName, event.toLastName)

  switch (event.eventType) {
    case 'status_changed': {
      const data = event.data ?? {}
      const toStatus = typeof data['to_status'] === 'string' ? data['to_status'] : null
      const fromStatus = typeof data['from_status'] === 'string' ? data['from_status'] : null
      /**
       * ⚠️ A `status_changed` WITH NO `to_status` IS `unknown`, NOT A BLANK LINE.
       * The payload is jsonb and nothing at the database level requires the key,
       * so this is the one place a malformed row can arrive. Falling through to
       * `unknown` names the event type on screen; rendering it as a status change
       * to nothing would be a line that says a conversation became blank.
       */
      if (toStatus === null) return { kind: 'unknown', eventType: event.eventType, actor }
      return { kind: 'statusChanged', from: fromStatus, to: toStatus, actor }
    }

    case 'assigned':
      // `from` is null on the FIRST assignment and a name on a handover. The
      // component renders two different sentences; this says which.
      return { kind: 'assigned', from, to, actor }

    case 'unassigned':
      return { kind: 'unassigned', from, actor }

    default:
      return { kind: 'unknown', eventType: event.eventType, actor }
  }
}
